import { Button, ClassCard, TabBar, TabItem } from '@wot/components';
import { ISession, ISessionAttendance } from '@wot/typings';
import { utcToZonedTime } from 'date-fns-tz';
import { useEffect, useState } from 'react';
import { MdClose, MdOutlineDateRange, MdOutlineList } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { plansApi } from '../../api/plan';
import { sessionsApi } from '../../api/sessions';
import { DEFAULT_PROFILE_PIC } from '../../assets/icons';
import { ERROR } from '../../assets/images';
import BottomSheet from '../../components/Bottomsheet';
import Calender from '../../components/Calender/Calender';
import CalenderProvider from '../../components/Calender/CalenderContext';
import { useAppSelector } from '../../hooks';
import { useTab } from '../../hooks/useTab';
import { API_URL, MEETING_DOMAIN } from '../../utils/constants';
import { ITimeSlot, SelectSlot } from '../TutorProfile/Book-trial';
import { SEO } from '../../components/SEO';

export default function MyCalender() {
  const { activeTab, onTabChange } = useTab('all');
  const { userData } = useAppSelector((state) => state.userSlice);
  const [classes, setClasses] = useState<ISession[]>([]);
  const [view, setView] = useState<'calender' | 'list'>('list');

  useEffect(() => {
    if (userData?._id) {
      sessionsApi
        .get(userData?._id)
        .then((data) => {
          setClasses(data);
        })
        .catch((err) => err);
    }
  }, []);

  return (
    <>
    <SEO 
      title="My Calendar - Schedule & Classes"
      description="View your tutoring schedule and upcoming classes on your personal calendar."
      keywords="my calendar, schedule, classes, tutoring schedule"
      url="/my-calender"
      type="website"
      noIndex={true}
    />
    <div className="w-full h-full min-h-screen bg-[#F5F6F9]">
      <div className="w-[90%] h-full mx-auto py-10">
        <div className="w-full h-full flex flex-col bg-white rounded-md">
          <h1 className="text-2xl font-semibold p-4">My Calender</h1>

          <div className="w-full border-b border-black">
            <div className="px-4">
              <TabBar
                value={activeTab}
                indicatorColor="bg-secondary"
                onChange={onTabChange}
                textColor="text-neutralGray1"
              >
                <TabItem label="All Classes" value="all" fontSize={15} />
                <TabItem label="Upcoming" value="upcoming" />
                <TabItem label="Completed" value="completed" />
                <TabItem label="Cancelled" value="cancelled" />
              </TabBar>
            </div>
          </div>

          <div className="w-full px-7 py-4 bg-neutralGray4 flex items-center gap-5">
            {/* <p className="text-base font-semibold">Filters</p> */}
            <div className="flex items-center rounded-md border ml-auto overflow-hidden">
              <div
                onClick={() => setView('calender')}
                className={`w-10 h-10 flex items-center justify-center ${view === 'calender' ? 'bg-primary text-white' : 'bg-white'
                  }`}
              >
                <MdOutlineDateRange className="w-4 h-4 cursor-pointer" />
              </div>
              <div
                onClick={() => setView('list')}
                className={`w-10 h-10 flex items-center justify-center ${view === 'list' ? 'bg-primary text-white' : 'bg-white'
                  }`}
              >
                <MdOutlineList className="w-4 h-4 cursor-pointer" />
              </div>
            </div>
          </div>

          {view === 'calender' ? (
            <div className="flex p-7">
              <CalenderProvider>
                <Calender
                  bookedSlots={classes.map((session) => ({
                    status: session.status,
                    tutor: session.tutor,
                    type: session.type,
                    category: session.category,
                    slot: session.scheduledTime
                  }))}
                />
              </CalenderProvider>
            </div>
          ) : (
            <div className="flex flex-col gap-4 my-10 px-4">
              {activeTab === 'all' ? (
                classes.length === 0 ? (
                  <div className="w-full flex flex-col items-center justify-center gap-7 py-10">
                    <h1 className="text-2xl font-semibold">{`No classes yet!`}</h1>
                  </div>
                ) : (
                  classes.map((session) => <ClassItem key={session._id} session={session} />)
                )
              ) : classes.filter((plan) => plan.status === activeTab).length === 0 ? (
                <div className="w-full flex flex-col items-center justify-center gap-7 py-10">
                  <h1 className="text-2xl font-semibold">{`No ${activeTab} classes yet!`}</h1>
                </div>
              ) : (
                classes
                  .filter((c) => c.status === activeTab)
                  .map((session) => <ClassItem key={session._id} session={session} />)
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export function ClassItem({ session: s }: { session: ISession }) {
  const { userData } = useAppSelector((state) => state.userSlice);
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const [showReschedulePrompt, setShowReschedulePrompt] = useState(false);
  const [showCalender, setShowCalender] = useState(false);
  const [showMarkAttendance, setShowMarkAttendance] = useState(false);
  const [newSlot, setNewSlot] = useState<ITimeSlot>();
  const [session, setSession] = useState(s);
  const [loading, setLoading] = useState(false);
  const { timezone } = useAppSelector((state) => state.preferencesSlice);
  const [showAttendanceIssue, setShowAttendanceIssue] = useState(false);
  const [issueDescription, setIssueDescription] = useState('');

  async function getSignature(meetId: number, role: number) {
    return await fetch(`${API_URL}/u/get-signature`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        meetId: meetId,
        role: role
      })
    })
      .then((res) => res.json())
      .then((res) => res.data)
      .catch((error) => error);
  }
  async function onJoin(meetId: number) {
    const data = await getSignature(meetId, 0);
    window.location.assign(
      `${MEETING_DOMAIN}?meetId=${meetId}&signature=${data.signature}&userName=${userData?.name}&userEmail=${userData?.email}&leaveUrl=${window.location.href}`
    );
  }

  async function onCancel() {
    if (userData?._id) {
      setLoading(true);
      await sessionsApi
        .update(userData?._id, session._id, {
          status: 'cancelled',
          cancelledBy: 'user'
        })
        .then(() => {
          setSession({
            ...session,
            status: 'cancelled',
            cancelledBy: 'user'
          });
          setShowCancelPrompt(false);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }

  async function onRescedule() {
    if (userData?._id) {
      if (!newSlot) {
        toast.error('Please select day and time for trial lesson');
        return;
      }
      setLoading(true);
      await sessionsApi
        .update(userData?._id, session._id, {
          status: 'upcoming',
          rescheduledBy: 'user',
          scheduledTime: newSlot,
          actionRequired: session.actionRequired || undefined
        })
        .then(() => {
          setSession({
            ...session,
            status: 'upcoming',
            rescheduledBy: 'user',
            scheduledTime: newSlot,
            actionRequired: undefined
          });
          setShowCalender(false);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }

  async function onMarkAttendance(attendance: ISessionAttendance) {
    if (userData?._id) {
      setLoading(true);
      await sessionsApi
        .update(userData?._id, session._id, {
          status: 'completed',
          attendance: {
            ...session.attendance,
            tutor: attendance
          },
          actionRequired: session.attendance.student !== 'pending' ? '' : session.actionRequired
        })
        .then(() => {
          setSession({
            ...session,
            attendance: {
              ...session.attendance,
              tutor: attendance
            },
            actionRequired: undefined
          });
          setShowMarkAttendance(false);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }

  async function onAttendanceIssue() {
    if (userData?._id) {
      if (!issueDescription) {
        toast.error(`Please write a description`);
        return;
      }
      setLoading(true);
      await sessionsApi
        .update(userData?._id, session._id, {
          status: session.status,
          dispute: {
            subject: 'Attendance Issue',
            description: issueDescription,
            status: 'active',
            raisedBy: 'user',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        })
        .then(() => {
          setSession({
            ...session,
            dispute: {
              subject: 'Attendance Issue',
              description: issueDescription,
              status: 'active',
              raisedBy: 'user',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
          });
          toast.success(`Issue raised for class Id: ${session._id}`);
          setShowAttendanceIssue(false);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }

  return (
    <>
      <ClassCard
        _id={session._id}
        type={session.type}
        category={session.category}
        endTime={utcToZonedTime(session.scheduledTime.end, timezone)}
        startTime={utcToZonedTime(session.scheduledTime.start, timezone)}
        name={session.tutor?.name || ''}
        profilePic={session.tutor?.profilePic || DEFAULT_PROFILE_PIC}
        status={session.status}
        showRescheduleBtn={!session.meeting && session.status === 'upcoming'}
        showJoinBtn={(session.meeting ? true : false) && session.status === 'upcoming'}
        showCancelBtn={session.status === 'upcoming' && !session.meeting}
        showScheduleBtn={session.actionRequired === 'schedule'}
        actionRequired={
          session.actionRequired === 'mark-attendance'
            ? session.attendance.tutor === 'pending' && session.actionRequired
              ? session.actionRequired
              : undefined
            : session.actionRequired
        }
        onJoin={() => onJoin(session.meeting.id)}
        onCancel={() => setShowCancelPrompt(true)}
        onRescedule={() => setShowReschedulePrompt(true)}
        onSchedule={() => setShowCalender(true)}
        showMarkAttendanceBtn={
          session.type === 'plan' &&
          session.actionRequired === 'mark-attendance' &&
          session.attendance.tutor === 'pending'
        }
        onMarkAttendance={() => setShowMarkAttendance(true)}
        rescheduled={session.rescheduledBy}
        attendance={session.attendance && session.attendance.student}
        showKebabMenu={session.status === 'completed' && !session.dispute}
        onAttendanceIssue={() => setShowAttendanceIssue(true)}
      />
      {showCancelPrompt && (
        <CancelClassModal
          loading={loading}
          onCancel={onCancel}
          planId={session.planId}
          setShowCancelPrompt={setShowCancelPrompt}
        />
      )}
      {showReschedulePrompt && (
        <RescheduleClassModal
          planId={session.planId}
          setShowReschedulePrompt={setShowReschedulePrompt}
          loading={loading}
          onClick={() => {
            setShowReschedulePrompt(false);
            setShowCalender(true);
          }}
        />
      )}
      {showCalender && (
        <BottomSheet>
          <div className="w-full h-full flex flex-col overflow-y-scroll">
            <div className="sticky top-0 w-full flex items-center px-7 md:px-10 py-7 bg-white">
              <div className="w-full flex items-center">
                <h1 className="w-full mx-auto text-base md:text-xl text-center font-semibold">
                  Select the day and time for your session
                </h1>
              </div>
              <MdClose className="right-0 text-2xl cursor-pointer ml-auto" onClick={() => setShowCalender(false)} />
            </div>
            <div className="w-full h-full md:w-4/5 mx-auto flex justify-center px-10 md:px-14 py-4">
              <SelectSlot onSubmit={(slot: any) => setNewSlot(slot)} tutorId={session.tutorId} />
            </div>
            <div className="w-full flex items-center justify-center border-t border-neutralGray3 p-7 mt-auto">
              <div className="w-full max-w-[164px] mx-auto">
                <Button loading={loading} text={'Confirm'} onClick={() => onRescedule()} />
              </div>
            </div>
          </div>
        </BottomSheet>
      )}
      {showMarkAttendance && (
        <BottomSheet height={50}>
          <div className="w-full h-full flex flex-col overflow-y-scroll">
            <div className="sticky top-0 w-full flex items-center px-7 md:px-10 py-7 bg-white">
              <div className="w-full flex items-center">
                <h1 className="w-full mx-auto text-base md:text-xl text-center font-semibold">Mark Tutor Attendance</h1>
              </div>
              <MdClose
                className="right-0 text-2xl cursor-pointer ml-auto"
                onClick={() => setShowMarkAttendance(false)}
              />
            </div>
            <div className="w-full flex items-center justify-center p-7">
              <div className="w-full max-w-xs flex items-center gap-4 p-10">
                <Button block text="Absent" type="p-outlined" onClick={() => onMarkAttendance('absent')} />
                <Button block text="Present" type="primary" onClick={() => onMarkAttendance('present')} />
              </div>
            </div>
          </div>
        </BottomSheet>
      )}
      {showAttendanceIssue && (
        <BottomSheet height={50}>
          <div className="w-full h-full flex flex-col overflow-y-scroll">
            <div className="sticky top-0 w-full flex items-center px-7 md:px-10 py-7 bg-white">
              <div className="w-full flex items-center">
                <h1 className="w-full mx-auto text-base text-center font-semibold">
                  Attendance Support for Class ID: {session._id}
                </h1>
              </div>
              <MdClose
                className="right-0 text-2xl cursor-pointer ml-auto"
                onClick={() => setShowAttendanceIssue(false)}
              />
            </div>

            <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-7">
              <div className="w-full max-w-5xl h-full grow">
                <textarea
                  onChange={(e) => setIssueDescription(e.target.value)}
                  cols={10}
                  className="w-full h-full p-7 bg-neutralBlue2 rounded-md"
                  placeholder="Write Your Description"
                ></textarea>
              </div>
              <div className="w-full max-w-[128px] flex items-center gap-4">
                <Button
                  disabled={!issueDescription}
                  block
                  text="Submit"
                  type="primary"
                  onClick={() => onAttendanceIssue()}
                />
              </div>
            </div>
          </div>
        </BottomSheet>
      )}
    </>
  );
}

function CancelClassModal({
  setShowCancelPrompt,
  loading,
  onCancel,
  planId
}: {
  setShowCancelPrompt: any;
  loading: boolean;
  onCancel: () => void;
  planId: string;
}) {
  const [noOfCancels, setNoOfCancels] = useState({ total: 0, occured: 0 });
  const { userData } = useAppSelector((state) => state.userSlice);
  useEffect(() => {
    if (!userData) {
      return;
    }
    plansApi.getById(userData?._id, planId).then((data) => {
      const p = data[0];
      setNoOfCancels(p.noOfCancelsByStudent);
    });
  }, [planId]);

  return (
    <BottomSheet height={50}>
      <div className="w-full h-full flex flex-col gap-7">
        <div className="sticky top-0 w-full flex flex-col items-center px-7 md:px-10 py-7 bg-white">
          <MdClose className="right-0 text-2xl cursor-pointer ml-auto" onClick={() => setShowCancelPrompt(false)} />
        </div>
        <div className="w-full flex flex-col items-center gap-7">
          <img src={ERROR} className="w-14 h-14" alt="error" />
          <div className="flex flex-col gap-4">
            <h1 className="text-base md:text-2xl font-semibold text-center">
              Are you sure you want to cancel the class?
            </h1>
            <p className="text-sm  text-center">
              You have {noOfCancels.total - noOfCancels.occured} cancellations left
            </p>
          </div>
          {noOfCancels.occured !== noOfCancels.total ? (
            <div className="w-full max-w-[164px] mt-4 flex items-center justify-center">
              <Button loading={loading} block text="Confirm" onClick={() => onCancel()} />
            </div>
          ) : (
            <Link to={'/contact'}>
              <div className="w-full max-w-[164px] mt-4 flex items-center justify-center">
                <Button type="secondary" block text="Contact Support" onClick={() => { }} />
              </div>
            </Link>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}

function RescheduleClassModal({
  setShowReschedulePrompt,
  loading,
  onClick,
  planId
}: {
  setShowReschedulePrompt: any;
  loading: boolean;
  onClick: () => void;
  planId: string;
}) {
  const [noOfReschedules, setNoOfReschedules] = useState({ total: 0, occured: 0 });
  const { userData } = useAppSelector((state) => state.userSlice);
  useEffect(() => {
    if (!userData) {
      return;
    }
    plansApi.getById(userData?._id, planId).then((data) => {
      const p = data[0];
      setNoOfReschedules(p.noOfReschedulesByStudent);
    });
  }, [planId]);

  return (
    <BottomSheet height={50}>
      <div className="w-full h-full flex flex-col gap-7">
        <div className="sticky top-0 w-full flex flex-col items-center px-7 md:px-10 py-7 bg-white">
          <MdClose className="right-0 text-2xl cursor-pointer ml-auto" onClick={() => setShowReschedulePrompt(false)} />
        </div>
        <div className="w-full flex flex-col items-center gap-7">
          <img src={ERROR} className="w-14 h-14" alt="error" />
          <div className="flex flex-col gap-4">
            <h1 className="text-base md:text-2xl font-semibold text-center">
              Are you sure you want to reschedule the class?
            </h1>
            <p className="text-sm  text-center">
              You have {noOfReschedules.total - noOfReschedules.occured} reschedules left
            </p>
          </div>
          {noOfReschedules.occured !== noOfReschedules.total ? (
            <div className="w-full max-w-[164px] mt-4 flex items-center justify-center">
              <Button loading={loading} block text="Confirm" onClick={() => onClick()} />
            </div>
          ) : (
            <Link to={'/contact'}>
              <div className="w-full max-w-[164px] mt-4 flex items-center justify-center">
                <Button type="secondary" block text="Contact Support" onClick={() => { }} />
              </div>
            </Link>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}
